import {
  type FuzzyIndex,
  type Normalizer,
  composeNormalizers,
  deStreetSuffixNormalizer,
  deUmlautNormalizer,
  lowerCaseNormalizer,
  removeCommonSeparators,
  stringSimilarity,
  stripDiacritics,
  trimNormalizer,
} from '@/helper/fuzzy';
import { SUGGESTION_LIMIT } from '@/helper/searchLimits';

/** Maximum number of suggestions rendered by the street field. */
export const STREET_SUGGESTION_LIMIT = SUGGESTION_LIMIT;
const LOCAL_FUZZY_LIMIT_MAX = 200;
const DEFAULT_THRESHOLD = 0.72;
const PREFIX_TYPO_THRESHOLD = 0.8;
const MIN_FUZZY_QUERY_LENGTH = 3;
const DIRECT_MATCH_MIN_SCORE = 0.88;

// German street-specific normalization pipeline
export const STREET_NORMALIZERS: Normalizer[] = [
  trimNormalizer,
  deStreetSuffixNormalizer, // Straße/Strasse/Str. -> strasse
  deUmlautNormalizer, // ä->ae, ö->oe, ü->ue, ß->ss
  stripDiacritics,
  lowerCaseNormalizer,
  removeCommonSeparators, // remove spaces, hyphens, dots, underscores
];

type ScoredStreet = { item: string; score: number };

export function mergeStreets(...lists: string[][]): string[] {
  const seen = new Set<string>();
  const merged: string[] = [];

  for (const list of lists) {
    for (const street of list) {
      if (seen.has(street)) continue;
      seen.add(street);
      merged.push(street);
    }
  }

  return merged;
}

export function findExactStreetMatches(find: string, streets: string[], normalizers: Normalizer[]): string[] {
  const trimmedFind = find.trim();
  if (!trimmedFind) return [];

  const normalizer = composeNormalizers(normalizers);
  const normalizedFind = normalizer(trimmedFind);
  const lowerFind = trimmedFind.toLowerCase();

  return streets.filter((street) => {
    if (street === trimmedFind || street.toLowerCase() === lowerFind) return true;
    return normalizer(street) === normalizedFind;
  });
}

export function findCanonicalStreet(find: string, streets: string[]): string | null {
  return findExactStreetMatches(find, streets, STREET_NORMALIZERS)[0] ?? null;
}

/** Guaranteed matches are listed first; fuzzy matches fill the remaining slots. */
export function prioritizeStreetSuggestions(
  merged: string[],
  guaranteedMatches: string[],
  uiLimit: number = STREET_SUGGESTION_LIMIT
): string[] {
  if (guaranteedMatches.length === 0) {
    return merged.slice(0, uiLimit);
  }

  const prioritized = mergeStreets(guaranteedMatches, merged);
  return prioritized.slice(0, uiLimit);
}

function scoreStreetMatchDirect(norm: string, qn: string): number {
  if (!qn) return 0;

  if (norm === qn) return 1;
  if (norm.startsWith(qn)) return 0.95;
  if (norm.includes(qn)) return 0.88;

  return 0;
}

function scoreStreetMatchFuzzy(norm: string, qn: string): number {
  const qLen = qn.length;
  if (qLen < MIN_FUZZY_QUERY_LENGTH) return 0;

  const sameLengthPrefix = norm.slice(0, qLen);
  const prefixTypoScore = stringSimilarity(qn, sameLengthPrefix);
  if (prefixTypoScore >= PREFIX_TYPO_THRESHOLD) return prefixTypoScore;

  const prefixWindow = norm.slice(0, Math.min(norm.length, qLen + 2));
  const prefixWindowScore = stringSimilarity(qn, prefixWindow);
  if (prefixWindowScore >= 0.7) return prefixWindowScore;

  if (Math.abs(norm.length - qLen) <= 5) {
    return stringSimilarity(qn, norm);
  }

  return 0;
}

function sortScoredStreets(results: ScoredStreet[]): ScoredStreet[] {
  return results.sort((a, b) => {
    if (b.score !== a.score) return b.score - a.score;
    return a.item.localeCompare(b.item, 'de');
  });
}

export function suggestStreetsLocal(
  term: string,
  index: FuzzyIndex<string>,
  normalizers: Normalizer[],
  opts: { limit?: number; threshold?: number } = {}
): string[] {
  const limit = opts.limit ?? STREET_SUGGESTION_LIMIT;
  const threshold = opts.threshold ?? DEFAULT_THRESHOLD;
  const normalizer = composeNormalizers(normalizers);
  const qn = normalizer(term ?? '');

  if (!qn) {
    return index.slice(0, limit).map((entry) => entry.item);
  }

  const direct: ScoredStreet[] = [];
  for (const entry of index) {
    const score = scoreStreetMatchDirect(entry.norm, qn);
    if (score >= DIRECT_MATCH_MIN_SCORE) {
      direct.push({ item: entry.item, score });
    }
  }

  if (direct.length >= limit) {
    return sortScoredStreets(direct)
      .slice(0, limit)
      .map((result) => result.item);
  }

  const directStreets = new Set(direct.map((result) => result.item));
  const fuzzy: ScoredStreet[] = [];

  for (const entry of index) {
    if (directStreets.has(entry.item)) continue;

    const score = scoreStreetMatchFuzzy(entry.norm, qn);
    if (score >= threshold) {
      fuzzy.push({ item: entry.item, score });
    }
  }

  return sortScoredStreets([...direct, ...fuzzy])
    .slice(0, limit)
    .map((result) => result.item);
}

export function searchStreetsLocal(
  find: string,
  cached: { streets: string[]; index: FuzzyIndex<string> },
  normalizers: Normalizer[] = STREET_NORMALIZERS
): string[] {
  const trimmedFind = find.trim();
  const exactMatches = findExactStreetMatches(trimmedFind, cached.streets, normalizers);
  const localLimit = Math.min(cached.streets.length, LOCAL_FUZZY_LIMIT_MAX);
  const localMatches = suggestStreetsLocal(trimmedFind, cached.index, normalizers, {
    limit: localLimit,
    threshold: DEFAULT_THRESHOLD,
  });
  const localGuaranteed = mergeStreets(exactMatches, localMatches);
  return prioritizeStreetSuggestions(localGuaranteed, exactMatches);
}
